import React, { useState } from 'react';
import PageTransition from '../components/PageTransition';

function TeamContact() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
    setSent(false);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError('Please complete all required fields.');
      return;
    }
    setLoading(true);
    setError('');
    setSent(false);
    try {
      const res = await fetch('https://formspree.io/f/mzzgabla', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          company: form.company,
          message: form.message
        })
      });
      if (res.ok) {
        setSent(true);
        setForm({ name: '', email: '', company: '', message: '' });
      } else {
        setError('There was an issue submitting your message. Please try again or email us directly.');
      }
    } catch {
      setError('There was an issue submitting your message. Please try again or email us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition>
      {/* Page header */}
      <div style={{
        background: 'linear-gradient(135deg, var(--clr-navy) 0%, var(--clr-navy-deep) 100%)',
        padding: '80px 24px 64px',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: 700,
          color: 'var(--clr-white)',
          marginBottom: 16,
        }}>Contact Us</h1>
        <p style={{
          fontSize: 'clamp(1rem, 2vw, 1.1rem)',
          color: 'rgba(255,255,255,0.75)',
          maxWidth: 500,
          margin: '0 auto',
          lineHeight: 1.6,
        }}>Schedule a consultation to discuss how we can support your financial objectives.</p>
      </div>

      {/* Split Layout */}
      <div style={{
        maxWidth: 1200, margin: '0 auto', padding: '64px 32px 100px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '64px',
        alignItems: 'start',
      }}>
        {/* Left: Info */}
        <div className="fade-in-up">
          <h2 style={{
            fontFamily: "var(--font-heading)",
            fontSize: 'clamp(2rem, 3.5vw, 2.5rem)',
            fontWeight: 700, color: 'var(--clr-navy)', marginBottom: 24,
          }}>Initiate a Consultation</h2>
          <p style={{fontSize:'1.05rem', color:'var(--clr-text-light)', lineHeight:1.8, marginBottom:48}}>
            Engage our team of specialists for comprehensive audit services, strategic tax planning, or outsourced financial leadership. Reach out to schedule a strictly confidential executive consultation.
          </p>



          <div style={{marginBottom:28}}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{
                width:40, height:40, borderRadius:'8px',
                background:'var(--clr-offwhite)', color:'var(--clr-primary)', border: '1px solid var(--clr-border)',
                display:'flex', alignItems:'center', justifyContent:'center',
                flexShrink:0,
              }}>
                <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
              </div>
              <div>
                <div style={{fontSize:'0.8rem', color:'var(--clr-gray-light)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:600, marginBottom:2}}>Business Hours</div>
                <div style={{color:'var(--clr-navy)', fontSize:'0.95rem', fontWeight:500}}>Monday - Friday, 9:00 AM - 6:00 PM ET</div>
              </div>
            </div>
          </div>

          <div style={{marginBottom:28}}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{
                width:40, height:40, borderRadius:'8px',
                background:'var(--clr-offwhite)', color:'var(--clr-primary)', border: '1px solid var(--clr-border)',
                display:'flex', alignItems:'center', justifyContent:'center',
                flexShrink:0,
              }}>
                <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="13,2 3,14 12,14 11,22 21,10 12,10 13,2"/>
                </svg>
              </div>
              <div>
                <div style={{fontSize:'0.8rem', color:'var(--clr-gray-light)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:600, marginBottom:2}}>Response Time</div>
                <div style={{color:'var(--clr-navy)', fontSize:'0.95rem', fontWeight:500}}>Typically within 24 business hours</div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div style={{
            marginTop: 40,
            padding: '24px',
            background: 'var(--clr-offwhite)',
            borderRadius: 'var(--radius-sm)',
            borderLeft: '4px solid var(--clr-accent-gold)',
          }}>
            <p style={{
              fontSize: '0.9rem',
              color: 'var(--clr-text-light)',
              lineHeight: 1.6,
              margin: 0,
              fontWeight: 500
            }}>
              Initial consultations are complimentary. All information shared is treated as strictly confidential in accordance with professional standards.
            </p>
          </div>
        </div>

        {/* Right: Form */}
        <div className="hover-lift fade-in-up" style={{
          background: 'var(--clr-white)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--clr-border)',
          padding: '48px 40px',
          boxShadow: 'var(--shadow-md)',
        }}>
          <h3 style={{
            fontFamily: "var(--font-heading)",
            fontSize: '1.4rem',
            fontWeight: 700,
            color: 'var(--clr-navy)',
            marginBottom: 32,
          }}>Send a Message</h3>
          <form className="byhw-contact-form" onSubmit={handleSubmit} style={{
            display:'flex', flexDirection:'column', gap:20,
          }}>
            <div>
              <label htmlFor="fullName" style={{display:'block', fontSize:'0.8rem', fontWeight:700, color:'var(--clr-text-light)', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.06em'}}>
                Full Name <span style={{color:'var(--clr-error, #dc2626)'}}>*</span>
              </label>
              <input
                id="fullName"
                type="text"
                name="name"
                placeholder="John Smith"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label htmlFor="email" style={{display:'block', fontSize:'0.8rem', fontWeight:700, color:'var(--clr-text-light)', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.06em'}}>
                Email Address <span style={{color:'var(--clr-error, #dc2626)'}}>*</span>
              </label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="john@company.com"
                value={form.email}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label htmlFor="company" style={{display:'block', fontSize:'0.8rem', fontWeight:700, color:'var(--clr-text-light)', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.06em'}}>
                Company
              </label>
              <input
                id="company"
                type="text"
                name="company"
                placeholder="Company name (optional)"
                value={form.company}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="message" style={{display:'block', fontSize:'0.8rem', fontWeight:700, color:'var(--clr-text-light)', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.06em'}}>
                Message <span style={{color:'var(--clr-error, #dc2626)'}}>*</span>
              </label>
              <textarea
                id="message"
                name="message"
                placeholder="Please describe your needs or questions..."
                value={form.message}
                onChange={handleChange}
                required
              />
            </div>
            <div role="alert" aria-live="polite">
              {error && <div style={{color:'var(--clr-error, #dc2626)', fontSize:'0.9rem', fontWeight:500, padding:'8px 0'}}>{error}</div>}
              {sent && <div style={{color:'var(--clr-success, #16a34a)', fontSize:'0.9rem', fontWeight:500, padding:'8px 0'}}>Thank you. Your message has been received. We will respond within one business day.</div>}
            </div>
            <button type="submit" className="byhw-cta" style={{
              marginTop:16, width:'100%', textAlign:'center',
            }} disabled={loading}>
              {loading ? 'Submitting...' : 'Request Executive Consultation'}
            </button>
          </form>
        </div>
      </div>

    </PageTransition>
  );
}

export default TeamContact;